import { FC, useEffect, useState } from "react"
import { useAppContext } from "../../../../system/providers/appContextProvider"
import { useTabsContext } from "../../../../system/providers/tabsProvider"
import { InvoiceCashOrderDataProvider } from "../../../../Services/DataProviders/InvoiceCashOrderDataProvider";
import { IInvoiceCashOrderCreateDTO, IInvoiceCashOrderGetDTO, IInvoiceCashOrderEditDTO } from "../../../../libs/coreapi-dto/documents/invoiceCashOrder";
import { IDocumentCreatorProps } from "../../../../@types/documents";
import { v4 as uuidv4 } from 'uuid';
import { DateTime } from "luxon";
import { useUserContext } from "../../../../system/providers/userContextProvider";
import { InvoiceCashOrderUpdateForm } from "../forms/InvoiceCashOrderUpdateForm";

interface IEditDocument {
    invoiceCashOrderDocument: IInvoiceCashOrderGetDTO
    documentEdit: IInvoiceCashOrderEditDTO
}

export const InvoiceCashOrderCreatorView: FC<IDocumentCreatorProps<IInvoiceCashOrderCreateDTO>> = (props) => {
    const appContext = useAppContext()
    const userContext = useUserContext()
    const tabsContext = useTabsContext()
    const invoiceCashOrderDP = new InvoiceCashOrderDataProvider(appContext.coreApiService);
    const [idGlobal, setIdGlobal] = useState(props.idGlobal)
    const [viewState, setViewState] = useState<GridStateType>(props.variant)
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [editDocument, setEditDocument] = useState<IEditDocument | null>(null);

    useEffect(() => {
        if (viewState === 'edit') { 
            invoiceCashOrderDP.getById(idGlobal as string, (invoiceCashOrderDocument) => {
                    const documentEdit: IInvoiceCashOrderEditDTO = {
                        idGlobal: invoiceCashOrderDocument.idGlobal as string,
                        documentDate: invoiceCashOrderDocument.documentDate.toF3DateTimeServerSide(userContext),
                        baseDocumentInfo : invoiceCashOrderDocument.baseDocumentInfo,
                        supplement : invoiceCashOrderDocument.supplement,
                        idTaxTypeGlobal : invoiceCashOrderDocument.taxType?.idGlobal ? invoiceCashOrderDocument.taxType.idGlobal : '',
                        baseText : invoiceCashOrderDocument.baseText,
                        idUserGlobal : invoiceCashOrderDocument.userAuthor?.idGlobal,
                        idContractsGlobal : invoiceCashOrderDocument.contracts?.idGlobal,
                        includeVat : invoiceCashOrderDocument.includeVat,
                        idContractorOwnerGlobal : invoiceCashOrderDocument.contractorOwner?.idGlobal,
                        idContractorFromGlobal : invoiceCashOrderDocument.contractorFrom?.idGlobal,
                        supplierSum : invoiceCashOrderDocument.supplierCostInfo?.sum,
                        supplierVatSum : invoiceCashOrderDocument.supplierCostInfo?.vatSum,
                        supplierSumIncVat : invoiceCashOrderDocument.supplierCostInfo?.sumIncVat,
                        retailSum : invoiceCashOrderDocument.retailCostInfo?.sum,
                        retailVatSum : invoiceCashOrderDocument.retailCostInfo?.vatSum,
                        retailSumIncVat : invoiceCashOrderDocument.retailCostInfo?.sumIncVat
                    }
                    setEditDocument({ invoiceCashOrderDocument: invoiceCashOrderDocument, documentEdit: documentEdit })
                
            })
        }
        else if (viewState === 'copy') {
            invoiceCashOrderDP.getById(idGlobal as string, (entity) => {
                let idGlobal = uuidv4()
                setEditDocument({
                    invoiceCashOrderDocument: 
                    { 
                        idGlobal: idGlobal, 
                        documentDate: DateTime.now().toF3DateTimeServerSide(userContext),
                        contractorOwner: entity.contractorOwner,
                        contractorFrom: entity.contractorFrom,
                        baseDocumentInfo : entity.baseDocumentInfo,
                        baseDocumentMnemocode : entity.baseDocumentMnemocode,
                        supplement : entity.supplement,
                        taxType : entity.taxType,
                        baseText : entity.baseText,
                        contracts : entity.contracts,
                        contractsNumber : entity.contractsNumber,
                        includeVat : entity.includeVat,
                        userAuthor : entity.userAuthor,
                        supplierCostInfo : entity.supplierCostInfo,
                        retailCostInfo : entity.retailCostInfo
                    } as IInvoiceCashOrderGetDTO,
                    documentEdit: 
                    { 
                        idGlobal: idGlobal, 
                        documentDate: DateTime.now().toF3DateTimeServerSide(userContext),
                        baseDocumentInfo : entity.baseDocumentInfo,
                        supplement : entity.supplement,
                        idTaxTypeGlobal : entity.taxType?.idGlobal ? entity.taxType.idGlobal : '',
                        baseText : entity.baseText,
                        idUserGlobal : entity.userAuthor?.idGlobal,
                        idContractsGlobal : entity.contracts?.idGlobal,
                        includeVat : entity.includeVat,
                        idContractorOwnerGlobal : entity.contractorOwner?.idGlobal,
                        idContractorFromGlobal : entity.contractorFrom?.idGlobal,
                        supplierSum : entity.supplierCostInfo?.sum,
                        supplierVatSum : entity.supplierCostInfo?.vatSum,
                        supplierSumIncVat : entity.supplierCostInfo?.sumIncVat,
                        retailSum : entity.retailCostInfo?.sum,
                        retailVatSum : entity.retailCostInfo?.vatSum,
                        retailSumIncVat : entity.retailCostInfo?.sumIncVat
                    } as IInvoiceCashOrderEditDTO
                })
            })

        }
    }, [viewState])

    return (
        <>
            {
                viewState === "create"
                    ?
                    <InvoiceCashOrderUpdateForm
                        isSubmitting={isSubmitting}
                        document={null}
                        documentItems={null}
                        documentUpdate={null}
                        idGlobal={props.idGlobal}
                        variant='create'
                        save={(invoiceCashOrder: IInvoiceCashOrderCreateDTO) => {
                            setIsSubmitting(true)
                            invoiceCashOrderDP.create(invoiceCashOrder, (id) => {
                                setIdGlobal(id)
                                setViewState('edit');
                                props.createdCallback?.()
                                setIsSubmitting(false)
                            }, () => { }, (e) => {
                                setIsSubmitting(false);
                            })
                        }}
                        ok={(invoiceCashOrder: IInvoiceCashOrderCreateDTO) => {
                            setIsSubmitting(true)
                            invoiceCashOrderDP.create(invoiceCashOrder, (id) => {
                                setIdGlobal(id)
                                setViewState("create")
                                props.createdCallback?.()
                                tabsContext.currentTab && tabsContext.close(tabsContext.currentTab)
                                setIsSubmitting(false)
                            }, () => { }, (e) => {
                                setIsSubmitting(false);
                            })
                        }}
                        cancel={() => tabsContext.currentTab && tabsContext.close(tabsContext.currentTab)}
                        permission={props.permission}
                    />
                    :
                    viewState === "edit" ?
                        editDocument && <InvoiceCashOrderUpdateForm
                            isSubmitting={isSubmitting}
                            variant='edit'
                            idGlobal={editDocument.documentEdit.idGlobal}
                            save={(invoiceCashOrder: IInvoiceCashOrderEditDTO) => {
                                setIsSubmitting(true)
                                invoiceCashOrderDP.update(idGlobal as string, invoiceCashOrder, (e) => {
                                    if (e.respType === 'isFailed') return setIsSubmitting(false);

                                    props.createdCallback?.()
                                    setIsSubmitting(false)
                                })
                            }}
                            ok={(invoiceCashOrder: IInvoiceCashOrderEditDTO) => {
                                setIsSubmitting(true);
                                invoiceCashOrderDP.update(idGlobal as string, invoiceCashOrder, (e) => {
                                    if (e.respType === 'isFailed') return setIsSubmitting(false);

                                    props.createdCallback?.()
                                    tabsContext.currentTab && tabsContext.close(tabsContext.currentTab)
                                    setIsSubmitting(false);
                                })
                            }}
                            cancel={() => tabsContext.currentTab && tabsContext.close(tabsContext.currentTab)}
                            document={editDocument.invoiceCashOrderDocument}
                            documentItems={null}
                            documentUpdate={editDocument.documentEdit}
                            permission={props.permission}
                        />
                        :
                        viewState === "copy" ?
                            editDocument && <InvoiceCashOrderUpdateForm
                                isSubmitting={isSubmitting}
                                variant='copy'
                                save={(invoiceCashOrder: IInvoiceCashOrderEditDTO) => {
                                    setIsSubmitting(true)
                                    invoiceCashOrderDP.create(invoiceCashOrder, (id) => {
                                        setIdGlobal(id)
                                        setViewState('edit')
                                        props.createdCallback?.()
                                        setIsSubmitting(false)
                                    }, () => { }, (e) => {
                                        setIsSubmitting(false);
                                    })
                                }}
                                ok={(invoiceCashOrder: IInvoiceCashOrderEditDTO) => {
                                    setIsSubmitting(true);
                                    invoiceCashOrderDP.create(invoiceCashOrder, (id) => {
                                        setIdGlobal(id)
                                        setViewState("create")
                                        props.createdCallback?.()
                                        tabsContext.currentTab && tabsContext.close(tabsContext.currentTab)
                                        setIsSubmitting(false);
                                    }, () => { }, (e) => {
                                        setIsSubmitting(false);
                                    })
                                }}
                                cancel={() => tabsContext.currentTab && tabsContext.close(tabsContext.currentTab)}
                                document={editDocument.invoiceCashOrderDocument}
                                documentItems={null}
                                documentUpdate={editDocument.documentEdit}
                                permission={props.permission}
                            />
                            :
                            <></>
            }
        </>
    );
}